// src/contexts/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  getRedirectResult,
  signInWithRedirect,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { auth, googleProvider, db, appCheck } from "../services/firebase";
import { getToken } from "firebase/app-check";
import {
  startActivityTracking,
  stopActivityTracking,
  isSessionExpired,
  clearActivity,
} from "../services/sessionActivity";
import * as Sentry from "@sentry/react"; // for React apps

const AuthContext = createContext();
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// Post-verification landing must be a route that actually exists — the old
// /onboarding target fell through the router's catch-all onto the marketing
// homepage, which read as "verification did nothing".
const emailVerificationSettings = {
  url: `${window.location.origin}/auth/action?continueUrl=${encodeURIComponent(
    `${window.location.origin}/dashboard`
  )}`,
  handleCodeInApp: true,
};
// Note: no dynamicLinkDomain here. Firebase Dynamic Links are shut down and
// the field expects a bare domain anyway — the old malformed value could make
// sendPasswordResetEmail reject outright. /login exists; /reset never did.
const passwordResetSettings = {
  url: `${window.location.origin}/login`,
  handleCodeInApp: true,
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Create /users/{uid} with retry; include isVerified from Auth */
async function createUserDocWithRetry(
  fbUser,
  extra = {},
  { retries = 3 } = {}
) {
  const uid = fbUser.uid;

  const safe = {
    uid,
    email: fbUser.email || "",
    displayName: fbUser.displayName || extra.displayName || "",
    photoURL: fbUser.photoURL || "",
    userType: "none",
    pendingVerfication: false,
    credits: 50,
    trustworthy: false,
    onboarding: true,
    isVerified: !!fbUser.emailVerified,
    createdAt: serverTimestamp(),
    ...extra,
  };

  // Never let client set this on create
  delete safe.emailVerifiedAt;
  delete safe.approved;

  try {
    await fbUser.getIdToken(true);
  } catch {}

  for (let i = 0; i < retries; i++) {
    try {
      await setDoc(doc(db, "users", uid), safe, { merge: true });
      return true;
    } catch (err) {
      if (
        err?.code === "permission-denied" ||
        err?.code === "unauthenticated"
      ) {
        await sleep(200 * (i + 1));
        continue;
      }
      throw err;
    }
  }
  // final attempt
  await setDoc(doc(db, "users", uid), safe, { merge: true });
  return true;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Auth user
  const [userProfile, setUserProfile] = useState(null); // /users/{uid}
  const [loading, setLoading] = useState(true);
  const creditsRef = useRef(userProfile?.credits);
  const creditsElRef = useRef(null);

  useEffect(() => {
    if (creditsElRef.current) {
      creditsElRef.current.textContent = String(creditsRef.current ?? 0);
    }
    // no deps: run once
  }, []);

  // helper to update UI without render
  const setCreditsUI = useCallback(
    (val) => {
      creditsRef.current = val; // store value (no render)
      if (creditsElRef.current) {
        creditsElRef.current.textContent = String(val); // update DOM directly
      }
    },
    [creditsRef]
  );

  const fetchUserProfile = useCallback(async (uid) => {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      if (snap.exists()) {
        setUserProfile(snap.data());
        creditsRef.current = Number(snap.data()?.credits);
        return true;
      }
      return false;
    } catch (err) {
      console.error("fetchUserProfile:", err);
      return false;
    }
  }, []);

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const eventUserProfile = useCallback(async () => {
    try {
      await delay(5000);
      const snap = await getDoc(doc(db, "users", userProfile?.uid));
      if (!snap.exists()) return null;
      const val = Number(snap.data()?.credits ?? 0);

      setCreditsUI(val);
      return val;
    } catch (err) {
      console.error("eventProfileRefetch", err);
      return null;
    }
  }, [userProfile?.uid]);

  const ensureUserDoc = useCallback(
    async (fbUser) => {
      const exists = await fetchUserProfile(fbUser.uid);
      if (!exists) {
        try {
          await createUserDocWithRetry(fbUser);
          await fetchUserProfile(fbUser.uid);
        } catch (err) {
          console.error("ensureUserDoc create failed:", err);
          Sentry.captureException(err, {
            tags: { section: "ensurueUserDocCreated" },
            extra: { userDoc: err },
          });
        }
      }
    },
    [fetchUserProfile]
  );

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (!result) return;

        const fbUser = result.user;
        await ensureUserDoc(fbUser);
        await fetchUserProfile(fbUser.uid);
      } catch (err) {
        console.error("Redirect sign-in error:", err);
        Sentry.captureException(err);
      }
    };

    handleRedirect();
  }, [ensureUserDoc, fetchUserProfile]);

  // Sign the user out when the rolling inactivity window (1 week) elapses.
  // State is reconciled by the onAuthStateChanged listener below once signOut
  // fires, so this just needs to tear down tracking and end the session.
  const handleInactivityLogout = useCallback(async () => {
    stopActivityTracking();
    clearActivity();
    try {
      await signOut(auth);
    } catch (err) {
      console.warn("inactivity logout failed:", err);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (!mounted) return;

      if (fbUser) {
        // Enforce the inactivity window on restored sessions: if the user
        // hasn't interacted for over a week, treat the persisted login as
        // expired and sign out instead of resuming the session.
        if (isSessionExpired()) {
          clearActivity();
          try {
            await signOut(auth);
          } catch (err) {
            console.warn("expired-session signOut failed:", err);
          }
          if (!mounted) return;
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        setUser(fbUser);
        // (Re)start activity tracking; refreshes the rolling window and arms
        // the timeout that signs the user out after a week of inactivity.
        startActivityTracking(handleInactivityLogout);
        await ensureUserDoc(fbUser);
        await fetchUserProfile(fbUser.uid);
        // Stamp lastLoginAt so the backend can run reactivation logic.
        // Best-effort — a failure here shouldn't block sign-in.
        try {
          await setDoc(
            doc(db, "users", fbUser.uid),
            { lastLoginAt: serverTimestamp() },
            { merge: true }
          );
        } catch (err) {
          console.warn("lastLoginAt write failed:", err);
        }
      } else {
        stopActivityTracking();
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => {
      mounted = false;
      unsub();
    };
  }, [ensureUserDoc, fetchUserProfile, handleInactivityLogout]);

  // Live-subscribe to /users/{uid} so credits and other profile fields
  // update across the app the moment the back-end debits them — no
  // refresh required.
  useEffect(() => {
    if (!user?.uid) return undefined;
    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        setUserProfile((prev) => ({ ...(prev || {}), ...data }));
        creditsRef.current = Number(data?.credits ?? 0);
      },
      (err) => {
        console.warn("user profile snapshot:", err);
      }
    );
    return unsub;
  }, [user?.uid]);

  const authTokenBearer = useCallback(async () => {
    if (!user) {
      throw new Error("Firebase error. No user detected.");
    }
    return user.getIdToken();
  }, [user]);

  const getAppCheckToken = useCallback(async () => {
    const { token } = await getToken(appCheck, false);
    return token;
  }, []);

  const resetPassword = useCallback(async (email) => {
    if (!email) throw new Error("Email is required");
    await sendPasswordResetEmail(auth, email, passwordResetSettings);
    return true;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    // Always let the user pick an account — avoids silent failures when several
    // Google sessions are signed in at once.
    try {
      googleProvider.setCustomParameters({ prompt: "select_account" });
    } catch {
      /* setCustomParameters is best-effort */
    }

    // On mobile, go straight to the full-page redirect flow. Mobile browsers —
    // and especially the in-app webviews users arrive from (LinkedIn, X,
    // Instagram, etc.) — block or mishandle OAuth popups, and a blocked popup
    // there often fails silently instead of throwing, so the popup→redirect
    // fallback below can't be relied on. Redirect now works on mobile because
    // the auth handler is served first-party from our own domain (see
    // services/firebase.js — authDomain matches the app origin), so the storage
    // the handshake needs is no longer dropped. getRedirectResult() in the
    // effect above completes the sign-in when the user returns to the app.
    const isMobile =
      typeof navigator !== "undefined" &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    if (isMobile) {
      await signInWithRedirect(auth, googleProvider);
      return;
    }

    // Desktop: keep the user on the page with the popup flow.
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await ensureUserDoc(result.user);
      return result;
    } catch (err) {
      // Popups can still be blocked, closed, or severed by browser
      // Cross-Origin-Opener-Policy. For those recoverable cases fall back to the
      // full-page redirect flow (picked up by getRedirectResult on return)
      // rather than dead-ending the user.
      const redirectFallbackCodes = new Set([
        "auth/popup-blocked",
        "auth/popup-closed-by-user",
        "auth/cancelled-popup-request",
        "auth/operation-not-supported-in-environment",
        "auth/web-storage-unsupported",
        "auth/internal-error",
      ]);
      if (redirectFallbackCodes.has(err?.code)) {
        console.warn(
          `Google popup sign-in failed (${err?.code}); falling back to redirect`
        );
        try {
          await signInWithRedirect(auth, googleProvider);
          return;
        } catch (redirectErr) {
          console.error("Google redirect sign-in error:", redirectErr);
          Sentry.captureException(redirectErr);
          throw redirectErr;
        }
      }
      console.error("Google sign-in error:", err);
      Sentry.captureException(err);
      throw err;
    }
  }, [ensureUserDoc]);

  const signUpWithEmail = useCallback(async (email, password, extra) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const fbUser = cred.user;

    if (extra?.displayName && !fbUser.displayName) {
      try {
        await updateProfile(fbUser, { displayName: extra.displayName });
        fbUser.displayName = extra.displayName;
      } catch (err) {
        Sentry.captureException(err, {
          tags: { section: "signUpWithEmail" },
          extra: { signUpError: err },
        });
        console.warn("updateProfile:", err);
      }
    }

    // create /users/{uid} with isVerified=false (until they click verification link)
    await createUserDocWithRetry(fbUser, extra);

    if (fbUser.email && !fbUser.emailVerified) {
      try {
        await sendEmailVerification(fbUser, emailVerificationSettings);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { section: "sendEmailVerfication" },
          extra: { signUpError: err },
        });
        console.warn("sendEmailVerification:", err);
      }
    }

    return cred;
  }, []);

  const signInWithEmail = useCallback(
    (email, password) => signInWithEmailAndPassword(auth, email, password),
    []
  );

  const fetchContent = useCallback(async () => {
    const uid = user?.uid;
    const userType = userProfile?.userType;
    if (!uid || !userType) return null;
    try {
      const snap = await getDoc(doc(db, `${userType}s`, uid));
      return snap.exists() ? snap.data() : null;
    } catch (err) {
      console.error("Failed to fetch content:", err);
      return null;
    }
  }, [user?.uid, userProfile?.userType]);

  const logout = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      stopActivityTracking();
      clearActivity();
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      if (options.redirectTo) window.location.assign(options.redirectTo);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: "logout" },
        extra: { Logout: err },
      });
      console.error("logout:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUserProfile = useCallback(
    async (updates) => {
      if (!user) return;
      const { isVerified, emailVerifiedAt, approved, ...safe } = updates || {};
      try {
        await setDoc(doc(db, "users", user.uid), safe, { merge: true });
        setUserProfile((prev) => ({ ...prev, ...safe }));
      } catch (err) {
        console.error("updateUserProfile:", err);
        Sentry.captureException(err, {
          tags: { section: "updateUserProfile" },
          extra: { setUser: err },
        });
        throw err;
      }
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      userProfile,
      loading,
      signInWithGoogle,
      signUpWithEmail,
      signInWithEmail,
      logout,
      updateUserProfile,
      fetchContent,
      resetPassword,
      authTokenBearer,
      getAppCheckToken,
      eventUserProfile,
      creditsRef,
      creditsElRef,
    }),
    [
      user,
      userProfile,
      loading,
      signInWithGoogle,
      signUpWithEmail,
      signInWithEmail,
      logout,
      updateUserProfile,
      fetchContent,
      resetPassword,
      authTokenBearer,
      getAppCheckToken,
      eventUserProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
